/* finger-chooser/index.js — the page: input, loop, settings, full screen,
 * the names mode and everything a person hears or reads.
 *
 * Input. Touch Events where the browser has them — every TouchEvent carries
 * the full list of touches still on the screen, and the round is reconciled
 * against it on each event, so a lost touchend cannot leave a ghost ring
 * holding the round forever (the «pulses and never picks» complaint about the
 * app-store finger choosers). Pointer Events are the fallback for touch
 * screens without Touch Events (Firefox on Windows).
 *
 * Mouse. A mouse does NOT place fingers. One pointer cannot hold several
 * spots at once, so «click to add a finger» would be a slower copy of the
 * names list without the thing that makes the game fair — everyone touching
 * at the same time. A computer gets the Names tab (same modes, same draw,
 * same animation style) and a code to open the page on a phone.
 */

import { MODES, COUNT_RANGE, DEFAULT_COUNT, clampCount, minParticipants, drawRound } from './draw.js';
import { createRound, PHASE, hopSchedule, hopAt } from './round.js';
import { drawFrame, revealTimes } from './render.js';
import { finger, team, SHAPES, FINGER } from './palette.js';
import * as sound from './sound.js';

const SLUG = 'finger-chooser';
const CFG = window.__FC_CFG || {};
const UI = CFG.ui || {};
const FINGER_NAMES = CFG.fingers || [];
const TEAM_NAMES = CFG.teams || [];

function t(key, vars) {
    let s = UI[key] != null ? String(UI[key]) : key;
    // Longest placeholder first: `:n` must not eat the start of `:need`.
    if (vars) for (const k of Object.keys(vars).sort((a, b) => b.length - a.length)) s = s.split(':' + k).join(String(vars[k]));
    return s;
}
function $(id) { return document.getElementById(id); }
function ae(name, data) { try { if (window.__ae) window.__ae(name, data); } catch (_) {} }
function stage(name) { try { if (window.__stage) window.__stage(name); } catch (_) {} }
function announce(msg) { try { if (window.__announce) window.__announce(msg); } catch (_) {} }
function fingerName(slot) {
    const s = Math.max(0, slot | 0);
    const base = FINGER_NAMES[s % FINGER.length] || ('#' + (s + 1));
    return s >= FINGER.length ? base + ' ' + (Math.floor(s / FINGER.length) + 1) : base;
}
function teamName(i) { return TEAM_NAMES[i] || String.fromCharCode(65 + i); }
function reducedMotion() {
    try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; }
}
function isIOS() {
    const ua = navigator.userAgent || '';
    return /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
}

/* ------------------------------------------------------------------ */
/* settings                                                             */
/* ------------------------------------------------------------------ */

const prefs = {
    tab: null, mode: 'winner',
    counts: Object.assign({}, DEFAULT_COUNT),
    hold: 2, sound: true, vib: true, seenMax: 0, players: 4,
};
function loadPrefs() {
    try {
        const ts = window.__toolSettings;
        if (!ts) return;
        const p = ts.load(SLUG, 'prefs', null);
        if (p && typeof p === 'object') {
            if (p.tab === 'fingers' || p.tab === 'names') prefs.tab = p.tab;
            if (MODES.indexOf(p.mode) >= 0) prefs.mode = p.mode;
            if (p.counts && typeof p.counts === 'object') {
                for (const m of MODES) if (p.counts[m] != null) prefs.counts[m] = clampCount(m, p.counts[m]);
            }
            if ([1, 2, 3].indexOf(Number(p.hold)) >= 0) prefs.hold = Number(p.hold);
            if (typeof p.sound === 'boolean') prefs.sound = p.sound;
            if (typeof p.vib === 'boolean') prefs.vib = p.vib;
            if (Number(p.seenMax) > 0) prefs.seenMax = Math.min(40, Math.floor(p.seenMax));
            if (Number(p.players) >= 2) prefs.players = Math.min(30, Math.floor(p.players));
        }
    } catch (_) {}
}
function savePrefs() {
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'prefs', prefs); } catch (_) {}
}
function countFor(mode) { return clampCount(mode, prefs.counts[mode]); }

/* ------------------------------------------------------------------ */
/* boot                                                                 */
/* ------------------------------------------------------------------ */

function boot() {
    const app = $('fcApp');
    if (!app || app.__fcBooted) return;
    app.__fcBooted = true;
    loadPrefs();

    const touchCapable = (navigator.maxTouchPoints || 0) > 0 || ('ontouchstart' in window);
    const reportedTP = navigator.maxTouchPoints || 0;
    const els = {
        tabF: $('fcTabF'), tabN: $('fcTabN'), paneF: $('fcPaneF'), paneN: $('fcPaneN'),
        soundBtn: $('fcSound'), fsBtn: $('fcFs'), fsLabel: $('fcFsLabel'),
        modes: Array.from(app.querySelectorAll('[data-fc-mode]')),
        countWrap: $('fcCountWrap'), countLabel: $('fcCountLabel'), count: $('fcCount'),
        minus: $('fcMinus'), plus: $('fcPlus'),
        field: $('fcField'), canvas: $('fcCanvas'), cap: $('fcCap'),
        idle: $('fcIdle'), idleSub: $('fcIdleSub'), noTouch: $('fcNoTouch'), qr: $('fcQr'), useNames: $('fcUseNames'),
        hold: $('fcHold'), vib: $('fcVib'), vibWrap: $('fcVibWrap'), info: $('fcTouchInfo'),
        names: $('fcNames'), players: $('fcPlayers'), pMinus: $('fcPMinus'), pPlus: $('fcPPlus'),
        fill: $('fcFill'), clear: $('fcClear'), toks: $('fcToks'), choose: $('fcChoose'),
        namesMsg: $('fcNamesMsg'), res: $('fcRes'), resH: $('fcResH'), resList: $('fcResList'), copy: $('fcCopy'),
    };
    const ctx = els.canvas.getContext('2d');

    sound.setEnabled(prefs.sound);
    sound.setHaptics(prefs.vib);

    const round = createRound({
        holdMs: prefs.hold * 1000,
        suspenseMs: reducedMotion() ? 350 : 1900,
        mode: prefs.mode,
        count: countFor(prefs.mode),
        onEvent: onRoundEvent,
    });

    /* ---------------- canvas size ---------------- */
    let W = 0, H = 0;
    function resize() {
        const r = els.field.getBoundingClientRect();
        W = Math.max(1, Math.round(r.width));
        H = Math.max(1, Math.round(r.height));
        const d = Math.min(2, window.devicePixelRatio || 1);
        els.canvas.width = Math.round(W * d);
        els.canvas.height = Math.round(H * d);
        ctx.setTransform(d, 0, 0, d, 0, 0);
        kick();
    }
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(resize).observe(els.field);
    else window.addEventListener('resize', resize);

    /* ---------------- loop ---------------- */
    let raf = 0;
    let lastCap = null;
    let flash = null;          // { text, until }
    let lastHop = -1, lastReveal = 0, lastTeamTick = -1;
    const env = { reduced: reducedMotion(), labels: { winner: t('label_winner'), loser: t('label_loser') } };
    try {
        matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
            env.reduced = e.matches;
            round.setConfig({ suspenseMs: e.matches ? 350 : 1900 }, performance.now());
        });
    } catch (_) {}

    function kick() { if (!raf) raf = requestAnimationFrame(frame); }
    function frame() {
        raf = 0;
        const now = performance.now();
        round.tick(now);
        const v = round.view(now);
        if (W > 1 && H > 1) drawFrame(ctx, W, H, v, now, env);
        suspenseCues(v);
        updateCaption(v, now);
        app.classList.toggle('fc-busy', round.busy);
        const physical = v.fingers.some((f) => f.state === 'down' || f.state === 'late');
        const animating = v.phase === PHASE.COUNT || v.phase === PHASE.SUSPENSE
            || ((v.phase === PHASE.WAIT) && !env.reduced)
            || ((v.phase === PHASE.RESULT || v.phase === PHASE.SHOWN) && v.sinceResult < 450)
            || (physical && !env.reduced && v.phase !== PHASE.RESULT)
            || (flash && now < flash.until);
        if (animating) kick();
    }

    function suspenseCues(v) {
        if (v.phase !== PHASE.SUSPENSE || !v.result) return;
        const res = v.result, ts = v.suspenseT;
        if (res.hops) {
            if (ts < res.hops.landAt) {
                const k = hopAt(res.hops, ts);
                if (k !== lastHop) { lastHop = k; sound.play('tick'); }
            }
        } else if (res.mode === 'order') {
            const times = revealTimes(res.ids.length, v.suspenseMs);
            let p = 0;
            for (let i = 0; i < times.length; i++) if (ts >= times[i]) p = i + 1;
            if (p > lastReveal) { lastReveal = p; sound.play('reveal', p); }
        } else if (res.mode === 'teams') {
            const k = Math.floor(ts / 120);
            if (ts < v.suspenseMs * 0.72 && k !== lastTeamTick) { lastTeamTick = k; sound.play('tick'); }
        }
    }

    /* ---------------- caption ---------------- */
    function modeSummary() {
        const m = round.config.mode, c = round.config.count;
        if (m === 'order') return t('sum_order');
        return t('sum_' + m, { n: c });
    }
    function resultCaption(res) {
        if (res.mode === 'winner') return res.chosenOrder.length > 1 ? t('cap_res_winners', { n: res.chosenOrder.length }) : t('cap_res_winner');
        if (res.mode === 'loser') return res.chosenOrder.length > 1 ? t('cap_res_losers', { n: res.chosenOrder.length }) : t('cap_res_loser');
        if (res.mode === 'teams') {
            const sizes = new Array(res.count).fill(0);
            for (const tm of res.teamOf.values()) sizes[tm]++;
            return sizes.map((n, i) => t('team_size', { team: teamName(i), n })).join(' · ');
        }
        return t('cap_res_order');
    }
    function updateCaption(v, now) {
        let text = '';
        const idle = v.phase === PHASE.IDLE && !v.fingers.length;
        if (flash && now < flash.until) text = flash.text;
        else if (v.phase === PHASE.WAIT) text = t('cap_wait', { n: v.n, need: v.need });
        else if (v.phase === PHASE.COUNT) text = t('cap_count');
        else if (v.phase === PHASE.SUSPENSE) text = t('cap_pick');
        else if (v.phase === PHASE.RESULT && v.result) text = resultCaption(v.result);
        else if (v.phase === PHASE.SHOWN && v.result) text = resultCaption(v.result) + ' · ' + t('cap_again');
        // A ring in the top band would sit under the caption: move it down,
        // unless the bottom band is taken too.
        let top = false, bottom = false;
        for (const f of v.fingers) {
            if (f.y < 96) top = true;
            if (f.y > H - 96) bottom = true;
        }
        const low = top && !bottom;
        if (low !== els.cap.classList.contains('fc-cap-low')) els.cap.classList.toggle('fc-cap-low', low);
        const key = text + '|' + idle;
        if (key === lastCap) return;
        lastCap = key;
        els.cap.textContent = text;
        els.cap.hidden = !text;
        els.idle.hidden = !idle || !touchCapable;
        els.idleSub.textContent = modeSummary() + ' · ' + t('cap_need', { n: v.need });
    }
    function showFlash(text, ms) {
        flash = { text, until: performance.now() + ms };
        lastCap = null;
        kick();
        setTimeout(kick, ms + 30);
    }

    /* ---------------- round events ---------------- */
    let firstFinger = true;
    let resultCount = 0;
    function onRoundEvent(type, data) {
        const now = performance.now();
        if (type === 'finger') {
            sound.play('finger', data.n);
            sound.buzz('finger');
            if (data.n > prefs.seenMax) { prefs.seenMax = data.n; savePrefs(); renderTouchInfo(); }
            if (firstFinger) { firstFinger = false; stage('fingers_down'); }
            wakeActivity();
        } else if (type === 'count-start') {
            flash = null;
        } else if (type === 'draw') {
            lastHop = -1; lastReveal = 0; lastTeamTick = -1;
        } else if (type === 'result') {
            onResult(data);
        } else if (type === 'late') {
            showFlash(t('cap_late'), 1600);
        } else if (type === 'cancel') {
            if (data.n >= 2) {
                showFlash(t('cap_cancel', { n: reportedTP || data.n }), 5000);
                sound.play('cancel');
                sound.buzz('cancel');
                try {
                    if (window.__aeDiagnostic) window.__aeDiagnostic({
                        issue: 'touches_dropped', n: data.n, reason: data.reason,
                        max_tp: reportedTP, seen: prefs.seenMax, ios: isIOS() ? 1 : 0,
                    });
                } catch (_) {}
            }
        }
        void now;
    }
    function onResult(res) {
        resultCount++;
        const m = res.mode;
        if (m === 'winner') sound.play('winner');
        else if (m === 'loser') sound.play('loser');
        else if (m === 'teams') sound.play('teams');
        else sound.play('winner');
        sound.buzz(m === 'loser' ? 'loser' : 'result');
        announce(announceFingers(res));
        try {
            if (window.__aeToolComplete) window.__aeToolComplete({
                via: 'fingers', mode: m, count: res.count, n: res.ids.length, round: resultCount,
            });
        } catch (_) {}
    }
    function announceFingers(res) {
        const nameOf = (id) => fingerName(res.at[id].slot);
        if (res.mode === 'winner' || res.mode === 'loser') {
            const who = res.chosenOrder.map(nameOf).join(', ');
            return t(res.mode === 'winner' ? 'an_winner' : 'an_loser', { x: who });
        }
        if (res.mode === 'teams') {
            const groups = [];
            for (let i = 0; i < res.count; i++) {
                const members = res.ids.filter((id) => res.teamOf.get(id) === i).map(nameOf);
                groups.push(teamName(i) + ': ' + members.join(', '));
            }
            return t('an_teams', { x: groups.join('; ') });
        }
        const byPlace = res.ids.slice().sort((a, b) => res.placeOf.get(a) - res.placeOf.get(b));
        return t('an_order', { x: byPlace.map((id, i) => (i + 1) + ' — ' + nameOf(id)).join(', ') });
    }

    /* ---------------- touch input ---------------- */
    const field = els.field;
    function local(pt) {
        const r = field.getBoundingClientRect();
        return { x: pt.clientX - r.left, y: pt.clientY - r.top };
    }
    function inField(list) {
        const ids = new Set();
        for (let i = 0; i < list.length; i++) {
            const tt = list[i];
            if (tt.target && field.contains(tt.target)) ids.add(tt.identifier);
        }
        return ids;
    }
    const useTouchEvents = typeof window.TouchEvent !== 'undefined' && ('ontouchstart' in window || reportedTP > 0);
    if (useTouchEvents) {
        field.addEventListener('touchstart', (e) => {
            if (e.cancelable) e.preventDefault();
            const now = performance.now();
            for (const tt of e.changedTouches) { const p = local(tt); round.down(tt.identifier, p.x, p.y, now); }
            round.sync(inField(e.touches), now);
            kick();
        }, { passive: false });
        field.addEventListener('touchmove', (e) => {
            if (e.cancelable) e.preventDefault();
            for (const tt of e.changedTouches) { const p = local(tt); round.move(tt.identifier, p.x, p.y); }
            kick();
        }, { passive: false });
        const end = (e) => {
            if (e.cancelable) e.preventDefault();
            const now = performance.now();
            const still = inField(e.touches);
            if (e.type === 'touchcancel' && still.size === 0) {
                round.cancelAll(now, 'touchcancel');
            } else {
                for (const tt of e.changedTouches) round.up(tt.identifier, now);
                round.sync(still, now);
            }
            // touchend is a user activation: from here on sound and vibration work.
            if (e.type === 'touchend') sound.prime();
            kick();
        };
        field.addEventListener('touchend', end, { passive: false });
        field.addEventListener('touchcancel', end, { passive: false });
    } else if (typeof window.PointerEvent !== 'undefined') {
        field.addEventListener('pointerdown', (e) => {
            if (e.pointerType === 'mouse') return;
            e.preventDefault();
            try { field.setPointerCapture(e.pointerId); } catch (_) {}
            const p = local(e);
            round.down('p' + e.pointerId, p.x, p.y, performance.now());
            kick();
        });
        field.addEventListener('pointermove', (e) => {
            if (e.pointerType === 'mouse') return;
            const p = local(e);
            round.move('p' + e.pointerId, p.x, p.y);
            kick();
        });
        const pend = (e) => {
            if (e.pointerType === 'mouse') return;
            round.up('p' + e.pointerId, performance.now());
            if (e.type === 'pointerup') sound.prime();
            kick();
        };
        field.addEventListener('pointerup', pend);
        field.addEventListener('pointercancel', pend);
    }
    // A mouse on a touch screen: say what is needed instead of doing nothing.
    field.addEventListener('mousedown', (e) => {
        if (!touchCapable) return;
        if (e.sourceCapabilities && e.sourceCapabilities.firesTouchEvents) return;
        showFlash(t('cap_mouse'), 3500);
    });
    field.addEventListener('contextmenu', (e) => e.preventDefault());
    // iOS Safari pinch-zoom lives outside touch-action on older versions.
    ['gesturestart', 'gesturechange'].forEach((ev) => field.addEventListener(ev, (e) => e.preventDefault()));

    document.addEventListener('visibilitychange', () => {
        const now = performance.now();
        if (document.visibilityState === 'hidden') {
            // An app switch swallows the touchends; the fingers are gone.
            round.cancelAll(now, 'hidden');
            releaseWake();
        } else {
            if (wakeWanted) acquireWake();
            kick();
        }
    });

    /* ---------------- wake lock ---------------- */
    // The shared primitive (public/js/wake-lock.js) keeps the screen on: it
    // re-verifies the hold after the page was hidden, which a private sentinel
    // cannot (WakeLockPrimitiveTest).
    let wakeWanted = false, wakeTimer = 0;
    function acquireWake() {
        try { if (window.__wakeLock && window.__wakeLock.hold) window.__wakeLock.hold('finger-chooser'); } catch (_) { /* optional */ }
    }
    function releaseWake() {
        try { if (window.__wakeLock && window.__wakeLock.releaseHold) window.__wakeLock.releaseHold('finger-chooser'); } catch (_) { /* optional */ }
    }
    function wakeActivity() {
        wakeWanted = true;
        acquireWake();
        clearTimeout(wakeTimer);
        wakeTimer = setTimeout(() => {
            if (!fsOn) { wakeWanted = false; releaseWake(); }
        }, 180000);
    }

    /* ---------------- full screen ---------------- */
    let fsOn = false, pseudo = false;
    function realFsEl() { return document.fullscreenElement || document.webkitFullscreenElement || null; }
    function setFsState(on) {
        fsOn = on;
        app.classList.toggle('fc-fs', on);
        els.fsBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        els.fsLabel.textContent = on ? t('fullscreen_exit') : t('fullscreen');
        els.fsBtn.setAttribute('aria-label', on ? t('fullscreen_exit') : t('fullscreen'));
        if (on) { wakeWanted = true; acquireWake(); }
        else wakeActivity();
        requestAnimationFrame(resize);
    }
    function pseudoOn() {
        pseudo = true;
        app.classList.add('fc-pseudo');
        document.documentElement.classList.add('fc-noscroll');
        setFsState(true);
    }
    function pseudoOff() {
        pseudo = false;
        app.classList.remove('fc-pseudo');
        document.documentElement.classList.remove('fc-noscroll');
        setFsState(false);
    }
    function enterFs() {
        ae('feature_use', { f: 'fc_fullscreen', tab: currentTab });
        if (typeof window.__safeFullscreen === 'function') {
            window.__safeFullscreen(app).then((ok) => { if (!ok) pseudoOn(); });
        } else {
            pseudoOn();
        }
    }
    function exitFs() {
        if (pseudo) { pseudoOff(); return; }
        const d = document;
        try {
            const p = d.exitFullscreen ? d.exitFullscreen() : (d.webkitExitFullscreen ? d.webkitExitFullscreen() : null);
            if (p && p.catch) p.catch(() => {});
        } catch (_) {}
    }
    els.fsBtn.addEventListener('click', () => { if (fsOn) exitFs(); else enterFs(); });
    ['fullscreenchange', 'webkitfullscreenchange'].forEach((ev) => document.addEventListener(ev, () => {
        if (!pseudo) setFsState(realFsEl() === app);
    }));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && pseudo) pseudoOff(); });

    /* ---------------- tabs ---------------- */
    let currentTab = prefs.tab || (touchCapable ? 'fingers' : 'names');
    function setTab(tab, byUser) {
        currentTab = tab;
        const f = tab === 'fingers';
        els.tabF.setAttribute('aria-selected', f ? 'true' : 'false');
        els.tabN.setAttribute('aria-selected', f ? 'false' : 'true');
        els.tabF.tabIndex = f ? 0 : -1;
        els.tabN.tabIndex = f ? -1 : 0;
        els.paneF.hidden = !f;
        els.paneN.hidden = f;
        app.dataset.tab = tab;
        if (f) { resize(); if (!touchCapable) drawQr(); }
        else { round.cancelAll(performance.now(), 'tab'); renderTokens(); }
        if (byUser) { prefs.tab = tab; savePrefs(); ae('feature_use', { f: 'fc_tab', tab }); }
    }
    els.tabF.addEventListener('click', () => setTab('fingers', true));
    els.tabN.addEventListener('click', () => setTab('names', true));
    [els.tabF, els.tabN].forEach((b) => b.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const next = currentTab === 'fingers' ? 'names' : 'fingers';
        setTab(next, true);
        (next === 'fingers' ? els.tabF : els.tabN).focus();
    }));
    if (els.useNames) els.useNames.addEventListener('click', () => { setTab('names', true); els.tabN.focus(); });

    /* ---------------- modes & count ---------------- */
    function renderModes() {
        for (const b of els.modes) b.setAttribute('aria-pressed', b.dataset.fcMode === prefs.mode ? 'true' : 'false');
        const m = prefs.mode;
        els.countWrap.hidden = m === 'order';
        if (m !== 'order') {
            els.countLabel.textContent = t('count_' + m);
            const c = countFor(m);
            els.count.textContent = String(c);
            els.minus.disabled = c <= COUNT_RANGE[m][0];
            els.plus.disabled = c >= COUNT_RANGE[m][1];
        }
    }
    function applyConfig() {
        renderModes();
        round.setConfig({ mode: prefs.mode, count: countFor(prefs.mode) }, performance.now());
        savePrefs();
        clearNamesResult();
        lastCap = null;
        kick();
    }
    for (const b of els.modes) b.addEventListener('click', () => {
        if (b.dataset.fcMode === prefs.mode) return;
        prefs.mode = b.dataset.fcMode;
        applyConfig();
    });
    els.minus.addEventListener('click', () => { prefs.counts[prefs.mode] = clampCount(prefs.mode, countFor(prefs.mode) - 1); applyConfig(); });
    els.plus.addEventListener('click', () => { prefs.counts[prefs.mode] = clampCount(prefs.mode, countFor(prefs.mode) + 1); applyConfig(); });

    /* ---------------- options ---------------- */
    els.hold.value = String(prefs.hold);
    els.hold.addEventListener('change', () => {
        const v = Number(els.hold.value);
        if ([1, 2, 3].indexOf(v) < 0) return;
        prefs.hold = v; savePrefs();
        round.setConfig({ holdMs: v * 1000 }, performance.now());
    });
    function renderSound() {
        els.soundBtn.setAttribute('aria-pressed', prefs.sound ? 'true' : 'false');
        els.soundBtn.dataset.on = prefs.sound ? '1' : '0';
    }
    els.soundBtn.addEventListener('click', () => {
        prefs.sound = !prefs.sound; savePrefs();
        sound.setEnabled(prefs.sound);
        renderSound();
        if (prefs.sound) { sound.prime(); sound.play('finger', 3); }
    });
    if (sound.hapticsSupported()) {
        els.vib.checked = prefs.vib;
        els.vib.addEventListener('change', () => {
            prefs.vib = els.vib.checked; savePrefs();
            sound.setHaptics(prefs.vib);
            if (prefs.vib) sound.buzz('finger');
        });
    } else {
        els.vibWrap.hidden = true;
    }
    // Any click on the tool is an activation: warm the audio up for the round.
    app.addEventListener('click', () => sound.prime(), true);

    /* ---------------- touch info ---------------- */
    function renderTouchInfo() {
        const lines = [];
        lines.push(t('touch_reported', { n: reportedTP }));
        lines.push(prefs.seenMax > 0 ? t('touch_seen', { n: prefs.seenMax }) : t('touch_seen_none'));
        if (isIOS()) lines.push(t('touch_ios'));
        lines.push(t('touch_rule'));
        els.info.textContent = lines.join(' ');
    }

    /* ---------------- QR for computers ---------------- */
    let qrDone = false;
    function drawQr() {
        if (qrDone || !els.qr || !CFG.qrSrc) return;
        qrDone = true;
        const url = location.origin + location.pathname;
        const paint = () => {
            const q = window.qrcode;
            if (typeof q !== 'function') return;
            try {
                const code = q(0, 'M');
                code.addData(url);
                code.make();
                const n = code.getModuleCount();
                const cell = Math.max(3, Math.floor(168 / (n + 8)));
                const size = (n + 8) * cell;
                const c = document.createElement('canvas');
                c.width = size; c.height = size;
                c.setAttribute('role', 'img');
                c.setAttribute('aria-label', t('notouch_qr'));
                const g = c.getContext('2d');
                g.fillStyle = '#ffffff'; g.fillRect(0, 0, size, size);
                g.fillStyle = '#000000';
                for (let r = 0; r < n; r++) for (let k = 0; k < n; k++) if (code.isDark(r, k)) g.fillRect((k + 4) * cell, (r + 4) * cell, cell, cell);
                els.qr.textContent = '';
                els.qr.appendChild(c);
                els.qr.hidden = false;
            } catch (_) {}
        };
        if (typeof window.qrcode === 'function') { paint(); return; }
        const s = document.createElement('script');
        s.src = CFG.qrSrc;
        const nonceEl = document.querySelector('script[nonce]');
        if (nonceEl && nonceEl.nonce) s.nonce = nonceEl.nonce;
        s.onload = paint;
        document.head.appendChild(s);
    }

    /* ---------------- names mode ---------------- */
    let namesAnim = 0;
    let namesRes = null;
    let namesSaveTimer = 0;
    function parseNames() {
        return (els.names.value || '').split(/\r?\n/).map((s) => s.trim().slice(0, 60)).filter(Boolean).slice(0, 100);
    }
    function svgShape(name) {
        return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' + SHAPES[name] + '"/></svg>';
    }
    function renderTokens() {
        cancelAnimationFrame(namesAnim); namesAnim = 0;
        const list = parseNames();
        els.toks.textContent = '';
        list.forEach((name, i) => {
            const sw = finger(i);
            const el = document.createElement('li');
            el.className = 'fc-tok';
            el.style.setProperty('--c', sw.hex);
            el.style.setProperty('--ci', sw.ink);
            el.innerHTML = '<span class="fc-tok-ring">' + svgShape(sw.shape) + '</span><span class="fc-tok-badge" aria-hidden="true"></span>';
            const nm = document.createElement('span');
            nm.className = 'fc-tok-name';
            nm.textContent = name;
            el.appendChild(nm);
            els.toks.appendChild(el);
        });
        els.toks.dataset.n = String(list.length);
        els.choose.disabled = false;
        els.namesMsg.textContent = '';
        els.namesMsg.hidden = true;
        clearNamesResult();
        return list;
    }
    function clearNamesResult() {
        cancelAnimationFrame(namesAnim); namesAnim = 0;
        namesRes = null;
        els.res.hidden = true;
        els.toks.classList.remove('is-drawing', 'is-done');
        for (const el of els.toks.children) {
            el.className = 'fc-tok';
            const sw = finger(Array.prototype.indexOf.call(els.toks.children, el));
            el.style.setProperty('--c', sw.hex);
            el.style.setProperty('--ci', sw.ink);
            el.querySelector('.fc-tok-badge').textContent = '';
        }
        // A mode switch in the middle of the reveal cancels it: the button
        // must come back with it.
        els.choose.disabled = false;
        els.choose.textContent = t('choose');
    }
    function namesChoose() {
        const list = parseNames();
        const mode = prefs.mode, count = countFor(mode);
        const need = minParticipants(mode, count);
        if (list.length < need) {
            els.namesMsg.textContent = t('names_need', { n: need });
            els.namesMsg.hidden = false;
            try { if (window.__toast) window.__toast(t('names_need', { n: need }), 'warning'); } catch (_) {}
            return;
        }
        if (els.toks.children.length !== list.length) renderTokens();
        clearNamesResult();
        stage('names_draw');
        const res = drawRound(mode, list.length, count);
        const S = reducedMotion() ? 0 : 1900;
        const toks = Array.from(els.toks.children);
        const hops = res.chosen.length ? hopSchedule(list.length, res.chosen[0], S) : null;
        const times = mode === 'order' ? revealTimes(list.length, S) : null;
        els.choose.disabled = true;
        els.toks.classList.add('is-drawing');
        const t0 = performance.now();
        let lastK = -1, lastP = 0, lastTT = -1;
        const step = () => {
            const ts = performance.now() - t0;
            if (ts >= S) { finishNames(list, res); return; }
            if (hops) {
                const k = hopAt(hops, ts);
                if (k !== lastK) {
                    lastK = k;
                    toks.forEach((el, i) => el.classList.toggle('is-lit', i === hops.hops[k].pos));
                    if (ts < hops.landAt) sound.play('tick');
                }
            } else if (times) {
                let p = 0;
                for (let i = 0; i < times.length; i++) if (ts >= times[i]) p = i + 1;
                if (p > lastP) {
                    for (let q = lastP + 1; q <= p; q++) {
                        const idx = res.placeOf.indexOf(q);
                        const el = toks[idx];
                        el.classList.add('is-placed');
                        el.querySelector('.fc-tok-badge').textContent = String(q);
                    }
                    lastP = p;
                    sound.play('reveal', p);
                }
            } else {
                const k = Math.floor(ts / 120);
                if (k !== lastTT) {
                    lastTT = k;
                    toks.forEach((el, i) => { el.style.setProperty('--c', team((k + i) % res.count).hex); });
                    sound.play('tick');
                }
            }
            namesAnim = requestAnimationFrame(step);
        };
        if (S === 0) finishNames(list, res);
        else namesAnim = requestAnimationFrame(step);
    }
    function finishNames(list, res) {
        namesAnim = 0;
        namesRes = { list, res };
        const toks = Array.from(els.toks.children);
        els.toks.classList.remove('is-drawing');
        els.toks.classList.add('is-done');
        toks.forEach((el) => el.classList.remove('is-lit'));
        const lines = [];
        let head = '';
        if (res.mode === 'winner' || res.mode === 'loser') {
            const chosen = new Set(res.chosen);
            toks.forEach((el, i) => {
                el.classList.add(chosen.has(i) ? 'is-chosen' : 'is-out');
                if (chosen.has(i)) el.querySelector('.fc-tok-badge').textContent = res.mode === 'winner' ? '★' : '✖';
            });
            const names = res.chosen.map((i) => list[i]);
            const key = res.mode + (names.length > 1 ? 's' : '');
            head = t('res_' + key, { x: names.join(', ') });
            lines.push(head);
            sound.play(res.mode === 'winner' ? 'winner' : 'loser');
        } else if (res.mode === 'teams') {
            toks.forEach((el, i) => {
                const tm = res.teamOf[i];
                const sw = team(tm);
                el.style.setProperty('--c', sw.hex);
                el.style.setProperty('--ci', sw.ink);
                el.classList.add('is-team');
                el.querySelector('.fc-tok-badge').textContent = teamName(tm);
                el.querySelector('.fc-tok-ring').innerHTML = svgShape(sw.shape);
            });
            head = t('res_teams_h');
            for (let tm = 0; tm < res.count; tm++) {
                const members = list.filter((_, i) => res.teamOf[i] === tm);
                lines.push(teamName(tm) + ': ' + members.join(', '));
            }
            sound.play('teams');
        } else {
            toks.forEach((el, i) => {
                el.classList.add('is-placed');
                el.querySelector('.fc-tok-badge').textContent = String(res.placeOf[i]);
                if (res.placeOf[i] === 1) el.classList.add('is-chosen');
            });
            head = t('res_order_h');
            const byPlace = list.map((name, i) => ({ name, p: res.placeOf[i] })).sort((a, b) => a.p - b.p);
            for (const x of byPlace) lines.push(x.p + '. ' + x.name);
            sound.play('winner');
        }
        els.resH.textContent = head;
        els.resList.textContent = '';
        const body = (res.mode === 'winner' || res.mode === 'loser') ? [] : lines;
        for (const ln of body) {
            const li = document.createElement('li');
            li.textContent = ln;
            els.resList.appendChild(li);
        }
        els.resList.hidden = !body.length;
        els.res.hidden = false;
        els.res.dataset.text = (res.mode === 'winner' || res.mode === 'loser') ? head : head + '\n' + lines.join('\n');
        els.choose.disabled = false;
        els.choose.textContent = t('choose_again');
        sound.buzz(res.mode === 'loser' ? 'loser' : 'result');
        announce(res.mode === 'winner' || res.mode === 'loser' ? head : head + ': ' + lines.join('; '));
        try { els.res.scrollIntoView({ block: 'nearest' }); } catch (_) {}
        try {
            if (window.__aeToolComplete) window.__aeToolComplete({ via: 'names', mode: res.mode, count: res.count, n: list.length });
        } catch (_) {}
    }
    els.choose.addEventListener('click', namesChoose);
    els.names.addEventListener('input', () => {
        clearTimeout(namesSaveTimer);
        namesSaveTimer = setTimeout(() => {
            try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'names', els.names.value.slice(0, 6000)); } catch (_) {}
            renderTokens();
        }, 200);
    });
    function renderPlayers() {
        els.players.textContent = String(prefs.players);
        els.pMinus.disabled = prefs.players <= 2;
        els.pPlus.disabled = prefs.players >= 30;
    }
    els.pMinus.addEventListener('click', () => { prefs.players = Math.max(2, prefs.players - 1); savePrefs(); renderPlayers(); });
    els.pPlus.addEventListener('click', () => { prefs.players = Math.min(30, prefs.players + 1); savePrefs(); renderPlayers(); });
    els.fill.addEventListener('click', () => {
        const a = [];
        for (let i = 1; i <= prefs.players; i++) a.push(t('player_n', { n: i }));
        els.names.value = a.join('\n');
        els.names.dispatchEvent(new Event('input'));
        renderTokens();
    });
    els.clear.addEventListener('click', () => {
        els.names.value = '';
        els.names.dispatchEvent(new Event('input'));
        renderTokens();
        els.names.focus();
    });
    els.copy.addEventListener('click', () => {
        const text = els.res.dataset.text || '';
        const ok = () => { try { if (window.__toast) window.__toast(t('copied'), 'success'); } catch (_) {} };
        const fail = () => { try { if (window.__toast) window.__toast(t('copy_failed'), 'error'); } catch (_) {} };
        // __copyText owns the WebView cases (API absent or permission-blocked →
        // execCommand fallback) and resolves to whether the text really went.
        try {
            const p = typeof window.__copyText === 'function'
                ? window.__copyText(text)
                : (navigator.clipboard ? navigator.clipboard.writeText(text).then(() => true) : Promise.resolve(false));
            Promise.resolve(p).then((done) => (done === false ? fail() : ok()), fail);
        } catch (_) { fail(); }
    });

    /* ---------------- initial render ---------------- */
    try {
        const saved = window.__toolSettings ? window.__toolSettings.load(SLUG, 'names', '') : '';
        els.names.value = typeof saved === 'string' && saved ? saved : '';
    } catch (_) {}
    if (!touchCapable) {
        els.noTouch.hidden = false;
        els.field.classList.add('fc-field-off');
    }
    renderModes();
    renderSound();
    renderPlayers();
    renderTouchInfo();
    renderTokens();
    setTab(currentTab, false);
    resize();
    // test seam: lets the e2e read the round without scraping the canvas
    window.__fcDebug = { view: () => round.view(performance.now()), round };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
